import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../components/LinkPreview.css';
import {
  DecoratorNode,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
  $applyNodeReplacement,
  $getNodeByKey,
} from 'lexical';
import { debounce, LinkPreview } from '../lib/links/shared';
import { getApiBase } from '../lib/apiBase';
import { sanitizeUrl } from '../lib/links/sanitize';
import { TIMING, SPACING, SIZING } from '../constants/ui';

export type SerializedLinkPreviewNode = Spread<
  {
    type: 'link-preview';
    version: 1;
    url: string;
    meta?: { title?: string | null; image?: string | null; hostname?: string; finalUrl?: string };
  },
  SerializedLexicalNode
>;

export class LinkPreviewNode extends DecoratorNode<JSX.Element> {
  __url: string;
  __meta?: { title?: string | null; image?: string | null; hostname?: string; finalUrl?: string };

  static getType(): string {
    return 'link-preview';
  }

  static clone(node: LinkPreviewNode): LinkPreviewNode {
    return new LinkPreviewNode(node.__url, node.__meta, node.__key);
  }

  constructor(url: string, meta?: { title?: string | null; image?: string | null; hostname?: string; finalUrl?: string }, key?: NodeKey) {
    super(key);
    this.__url = url;
    this.__meta = meta;
  }

  static importJSON(serializedNode: SerializedLinkPreviewNode): LinkPreviewNode {
    const { url, meta } = serializedNode;
    return new LinkPreviewNode(url, meta);
  }

  exportJSON(): SerializedLinkPreviewNode {
    return {
      type: 'link-preview',
      version: 1,
      url: this.__url,
      meta: this.__meta,
    };
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  isInline(): boolean {
    return true;
  }

  decorate(editor: LexicalEditor): JSX.Element {
    const nodeKey = this.getKey();
    return <LinkPreviewComponent nodeKey={nodeKey} url={this.__url} meta={this.__meta} editor={editor} />;
  }
}

export function $createLinkPreviewNode(url: string, meta?: { title?: string | null; image?: string | null; hostname?: string; finalUrl?: string }): LinkPreviewNode {
  return $applyNodeReplacement(new LinkPreviewNode(url, meta));
}

export function $isLinkPreviewNode(node: LexicalNode | null | undefined): node is LinkPreviewNode {
  return node instanceof LinkPreviewNode;
}

// Use the editor-registered class to avoid HMR type mismatches
export function $createLinkPreviewNodeFromEditor(editor: LexicalEditor, url: string, meta?: { title?: string | null; image?: string | null; hostname?: string; finalUrl?: string }) {
  // Note: This is accessing internal Lexical editor state for HMR compatibility
  // In production, this should work correctly with the registered node type
  const registry = (editor as unknown as { _nodes: Map<string, { klass: typeof LinkPreviewNode }> })._nodes;
  const def = registry?.get('link-preview');
  const Klass = def?.klass || LinkPreviewNode;
  return $applyNodeReplacement(new Klass(url, meta));
}

function LinkPreviewComponent({ nodeKey, url, meta, editor }: { nodeKey: NodeKey; url: string; meta?: { title?: string | null; image?: string | null; hostname?: string; finalUrl?: string }; editor: LexicalEditor }) {
  const [currentUrl, setCurrentUrl] = useState(url);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LinkPreview | null>(null);
  const cacheRef = useRef<Map<string, Promise<LinkPreview>>>(new Map());

  useEffect(() => {
    setCurrentUrl(url);
  }, [url]);

  const doFetch = useMemo(
    () =>
      debounce(async (u: string) => {
        const sanitized = sanitizeUrl(u);
        if (!sanitized) return;
        const canon = sanitized.toString();
        if (!cacheRef.current.has(canon)) {
          const p = fetch(`${getApiBase()}/api/link/preview?url=${encodeURIComponent(canon)}`)
            .then((r) => r.json())
            .then((j: LinkPreview) => j);
          cacheRef.current.set(canon, p);
        }
        setLoading(true);
        setError(null);
        try {
          const result = await cacheRef.current.get(canon)!;
          setData(result);
          editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            if (node && node instanceof LinkPreviewNode) {
              node.__meta = {
                title: result.title ?? null,
                image: result.image ?? null,
                hostname: result.hostname,
                finalUrl: result.finalUrl,
              };
            }
          });
        } catch (e: unknown) {
          const errorMessage = e instanceof Error ? e.message : 'Failed to load preview';
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      }, TIMING.LINK_PREVIEW_DEBOUNCE),
    [editor, nodeKey]
  );

  useEffect(() => {
    // fetch on mount and when url changes, debounced
    doFetch(currentUrl);
  }, [currentUrl, doFetch]);


  const removePreview = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey) as LinkPreviewNode | null;
      if (!node) return;
      node.remove();
    });
  };

  const onUrlChange = (value: string) => {
    setCurrentUrl(value);
    const sanitized = sanitizeUrl(value);
    const canon = sanitized ? sanitized.toString() : value;
    editor.update(() => {
      const node = $getNodeByKey(nodeKey) as LinkPreviewNode | null;
      if (!node) return;
      node.__url = canon;
    });
    doFetch(canon);
  };

  const hostname = meta?.hostname || (data?.hostname ?? '');
  const title = meta?.title ?? data?.title ?? null;
  const finalUrl = meta?.finalUrl ?? data?.finalUrl ?? currentUrl;
  const image = meta?.image ?? data?.image ?? null;

  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const linkRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Close popup when clicking outside (mobile-friendly)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        showPopup &&
        popupRef.current &&
        !popupRef.current.contains(target) &&
        linkRef.current &&
        !linkRef.current.contains(target)
      ) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      // Use both mouse and touch events for better mobile support
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [showPopup]);

  // Show popup on click only
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      e.preventDefault(); // Prevent default link behavior
      if (linkRef.current) {
        const rect = linkRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate popup position with mobile-friendly adjustments
        let top = rect.bottom + window.scrollY + SPACING.POPUP_OFFSET;
        let left = rect.left + window.scrollX;
        
        // Mobile viewport adjustments
        if (viewportWidth < SPACING.MOBILE_BREAKPOINT) {
          // Center popup on mobile
          left = Math.max(SPACING.POPUP_OFFSET, Math.min(left, viewportWidth - (SPACING.MOBILE_POPUP_MAX_WIDTH + SPACING.POPUP_PADDING)));
          // Adjust top if too close to bottom
          if (top + SIZING.POPUP_HEIGHT > viewportHeight + window.scrollY) {
            top = rect.top + window.scrollY - SIZING.POPUP_ABOVE_OFFSET; // Show above instead
          }
        }
        
        setPopupPosition({ top, left });
      }
      setShowPopup(true);
    };

    const handleFocus = () => {
      if (linkRef.current) {
        const rect = linkRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate popup position with mobile-friendly adjustments
        let top = rect.bottom + window.scrollY + SPACING.POPUP_OFFSET;
        let left = rect.left + window.scrollX;
        
        // Mobile viewport adjustments
        if (viewportWidth < SPACING.MOBILE_BREAKPOINT) {
          // Center popup on mobile
          left = Math.max(SPACING.POPUP_OFFSET, Math.min(left, viewportWidth - (SPACING.MOBILE_POPUP_MAX_WIDTH + SPACING.POPUP_PADDING)));
          // Adjust top if too close to bottom
          if (top + SIZING.POPUP_HEIGHT > viewportHeight + window.scrollY) {
            top = rect.top + window.scrollY - SIZING.POPUP_ABOVE_OFFSET; // Show above instead
          }
        }
        
        setPopupPosition({ top, left });
      }
      setShowPopup(true);
    };

    const handleBlur = () => {
      // Delay hiding to allow clicking on popup
      setTimeout(() => setShowPopup(false), TIMING.POPUP_CLOSE_DELAY);
    };

    const linkEl = linkRef.current;
    if (linkEl) {
      linkEl.addEventListener('click', handleClick);
      linkEl.addEventListener('focus', handleFocus);
      linkEl.addEventListener('blur', handleBlur);
      
      return () => {
        linkEl.removeEventListener('click', handleClick);
        linkEl.removeEventListener('focus', handleFocus);
        linkEl.removeEventListener('blur', handleBlur);
      };
    }
  }, []);

  const goToUrl = () => {
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  // Always show the original URL, not the fetched title
  const displayTitle = currentUrl;

  return (
    <>
      <span 
        ref={linkRef}
        tabIndex={0}
        contentEditable={true}
        suppressContentEditableWarning={true}
        onInput={(e) => {
          const newUrl = e.currentTarget.textContent || '';
          onUrlChange(newUrl);
        }}
        className="link-preview"
      >
        {displayTitle}
      </span>
      
      {/* Popup preview */}
      {showPopup && (
        <div
          ref={popupRef}
          className="link-preview-popup"
          style={{
            top: popupPosition.top,
            left: popupPosition.left,
            maxWidth: window.innerWidth < SPACING.MOBILE_BREAKPOINT ? Math.min(SPACING.MOBILE_POPUP_MAX_WIDTH, window.innerWidth - SPACING.MOBILE_POPUP_MARGIN) : SPACING.DESKTOP_POPUP_MAX_WIDTH
          }}
        >
          {image ? (
            <img 
              src={image} 
              alt="" 
              className="link-preview-image"
            />
          ) : (
            <div className="link-preview-placeholder" />
          )}
          
          <div className="link-preview-content">
            <div className="link-preview-header">
              <div className="link-preview-hostname">
                {hostname}
              </div>
              <button
                onClick={() => setShowPopup(false)}
                className="link-preview-close-button"
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="link-preview-title">
              {loading ? 'Loading…' : error ? 'Preview failed' : (title ?? finalUrl)}
            </div>
            
            <div className="link-preview-actions">
              <button
                onClick={goToUrl}
                className="link-preview-button primary"
              >
                Open
              </button>
              <button
                onClick={removePreview}
                className="link-preview-button secondary"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


